import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useGetRolesQuery, useGetUsersQuery, useUpdateSubscriptionMutation } from 'api/main';
import { userSchemas } from 'schemas';
import { ORDER_BY, roleCategories, sortDirection } from 'utils/constants';
import { parseFullName } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { ActionButton, CancelButton, CustomDialogTitle, CustomSelect, Loader } from 'components';
import { Dialog, DialogActions, DialogContent, MenuItem } from '@mui/material';

const ManagerDialog = ({ open, onClose, subscription }) => {
    const [selectedUser, setSelectedUser] = useState('');
    const [error, setError] = useState('');

    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const { data: { userList = [] } = { userList: [] }, isFetching: listLoader } = useGetUsersQuery(
        userSchemas.getUsers({
            disablePagination: true,
            orderBy: ORDER_BY.lastName,
            direction: sortDirection.ASC,
            role: roles.filter(r => r.category?.name === roleCategories.PROMOTERS).map(r => r.id),
        }),
        { skip: !roles.length }
    );

    const [updateSubscription, { isLoading: loading, isSuccess: assignSuccess, reset }] =
        useUpdateSubscriptionMutation();

    const handleConfirm = useCallback(() => {
        if (selectedUser) {
            setError('');
            if (subscription.internallyManagedBy?.id !== +selectedUser) {
                updateSubscription({
                    id: subscription.id,
                    internallyManagedBy: `api/users/${selectedUser}`,
                });
            } else {
                onClose();
            }
        } else {
            setError(texts.SERVICER_MANAGER_REQUIRED);
        }
    }, [selectedUser, subscription, updateSubscription, onClose]);

    const handleUserChange = useCallback(e => {
        setSelectedUser(e.target.value);
    }, []);

    useEffect(() => {
        if (!open) {
            // clear input when closed
            setSelectedUser('');
        } else if (subscription?.internallyManagedBy) {
            // populate the input when editing
            setSelectedUser(subscription.internallyManagedBy?.id);
        }
    }, [open, subscription?.internallyManagedBy]);

    useEffect(() => {
        if (assignSuccess) {
            reset();
            onClose();
        }
    }, [assignSuccess, reset, onClose]);

    return (
        <Dialog open={open}>
            <CustomDialogTitle title={texts.CHANGE_MANAGER} handleClose={onClose} />
            <DialogContent>
                <Loader loading={rolesLoader || listLoader}>
                    <CustomSelect
                        label={texts.SEARCH_FOR_SERVICER}
                        onChange={handleUserChange}
                        value={selectedUser}
                        error={!!error}
                        helperText={error}
                    >
                        {!userList.length && (
                            <MenuItem value={0} disabled>
                                {texts.NO_OPTIONS}
                            </MenuItem>
                        )}
                        {userList.map(u => (
                            <MenuItem key={u.id} value={u.id}>
                                {parseFullName(u)}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                </Loader>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={loading} onClick={onClose}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton onClick={handleConfirm} loading={loading}>
                    {texts.CONFIRMER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

ManagerDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    subscription: PropTypes.object,
};

export default ManagerDialog;
